import Checkbox from '@/components/Checkbox/Checkbox'
import Price from '@/components/Price'
import {USD_DISPLAY_DECIMALS} from '@/constants/config'
import type {GrubhubOrder, LineItem} from '@/lib/food-grubhub/foodGrubhubModel'
import useGrubhubGetAuth from '@/lib/food-grubhub/hooks/useGrubhubGetAuth'
import useGrubhubMenuItemDetail from '@/lib/food-grubhub/hooks/useGrubhubMenuItemDetail'
import {useGrubhubUserAddress} from '@/lib/food-grubhub/store'
import buildOptionsText from '@/lib/food-grubhub/utils/buildOptionsText'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'

import style from './FoodGrubhubOrderItemGroups.module.scss'

interface ItemProps {
  item: LineItem
  isSelectable?: boolean
  onSelect?: (item: LineItem, state: boolean) => void
  isDisabled?: boolean
  checkboxLabel?: string
  showPrice?: boolean
  showQuantity?: boolean
  showTrackingInfo?: boolean
  showOptions?: boolean
  showBuyAgain?: boolean
  order?: GrubhubOrder
  onOpenChange?: (isOpen: boolean) => void
}

const OrderItem = memo<ItemProps>(function OrderItem({
  item,
  isSelectable,
  onSelect,
  isDisabled,
  checkboxLabel,
  showPrice = true,
  showQuantity = true,
  // showTrackingInfo = true,
  showOptions = true,
  // showBuyAgain = false,
  // order,
  // onOpenChange,
}) {
  const onChange = useCallback(
    (value: boolean) => {
      onSelect?.(item, value)
    },
    [item, onSelect],
  )

  const {data: authResponse} = useGrubhubGetAuth()

  const [userAddress] = useGrubhubUserAddress()

  const timestampRef = useRef(Date.now())

  const categoryDetailQuery = {
    restaurantId: item.restaurantId,
    menuItemId: item.menuItemId,
    time: timestampRef.current,
    hideUnavailableMenuItems: true,
    orderType: 'standard',
    locationMode: 'DELIVERY',
    version: 4,
    includeWeightedItemsData: true,
    location: `POINT(${userAddress.longitude} ${userAddress.latitude})`,
  }

  const {data: menuItemDetail} = useGrubhubMenuItemDetail(
    categoryDetailQuery,
    authResponse?.session_handle.access_token ?? '',
  )

  const selectedOptionsText = buildOptionsText(menuItemDetail, item.options)

  return (
    <div className={clsx(style.Item, isDisabled && style.IsDisabled)}>
      {isSelectable && (
        <div className={style.Selection}>
          <Checkbox
            label={`${checkboxLabel} "${item.name}"`}
            onChange={onChange}
            isReadOnly={isDisabled}
          />
          {isDisabled && <VisuallyHidden>Not available for {checkboxLabel}</VisuallyHidden>}
        </div>
      )}
      <div className={style.ItemImage}>
        {menuItemDetail?.media_image && (
          <img
            src={`${menuItemDetail.media_image.base_url}${menuItemDetail.media_image.public_id}.${menuItemDetail.media_image.format}`}
            alt=''
            aria-hidden='true'
            loading='lazy'
          />
        )}
      </div>
      <div className={style.ItemInfo}>
        <h3 className={style.ItemTitle}>
          {/* <Link
            to={ProductRoute.fullPath}
            params={{
              productId: {
                retailer: item.retailer,
                id: item.id,
              },
            }}
            className={style.ItemTitle}
          > */}
          {item.name}
          {/* </Link> */}
        </h3>
        {/* TODO: add support for variations update */}
        {showOptions && item.options && (
          <div className={style.ItemOptions}>{selectedOptionsText}</div>
        )}
        {showPrice && (
          <div className={style.ItemPrice}>
            <VisuallyHidden>Price: </VisuallyHidden>
            <Price
              price={item.dinerTotal}
              options={{fractionDigits: USD_DISPLAY_DECIMALS, exactFractionDigits: true}}
            />
          </div>
        )}
      </div>
      {/* {showBuyAgain && order && (
        <div className={style.BuyAgainContainer}>
          <BuyAgainDialog
            order={order}
            preSelectedItemKey={`${item.retailer}-${item.id}`}
            onOpenChange={onOpenChange}
          >
            <PrimaryButton size='sm' className={style.BuyAgainButton}>
              Buy Again
            </PrimaryButton>
          </BuyAgainDialog>
        </div>
      )} */}
      {showQuantity && (
        <div className={style.ItemQuantity}>
          <VisuallyHidden>Quantity: </VisuallyHidden>
          {item.quantity}
        </div>
      )}
    </div>
  )
})

interface Props {
  items: LineItem[]
  isSelectable?: boolean
  isItemDisabled?: (item: LineItem) => boolean
  onSelect?: (item: LineItem, state: boolean) => void
  checkboxLabel?: string
  showRestaurant?: boolean
  showPrice?: boolean
  showQuantity?: boolean
  showTrackingInfo?: boolean
  showOptions?: boolean
  showBuyAgain?: boolean
  order?: GrubhubOrder
  onOpenChange?: (isOpen: boolean) => void
}

export default memo<Props>(function OrderItemGroups({
  items,
  isSelectable,
  isItemDisabled,
  onSelect,
  checkboxLabel,
  showRestaurant = true,
  showPrice = true,
  showQuantity = true,
  showTrackingInfo = true,
  showOptions = true,
  showBuyAgain = false,
  order,
  onOpenChange,
}) {
  return (
    <div className={style.ItemGroups} role='region' aria-label='Order items'>
      {items.map(item => (
        <div key={`${item.restaurantId}-${item.menuItemId}`}>
          <div className={style.ItemRetailer}>
            {showRestaurant &&
              // <img
              //   height={16}
              //   src={item.restaurantInfo?.name}
              //   loading='lazy'
              //   alt=''
              // />
              item.restaurantInfo?.name}
          </div>
          <OrderItem
            item={item}
            isSelectable={isSelectable}
            onSelect={onSelect}
            isDisabled={isItemDisabled?.(item) ?? false}
            checkboxLabel={checkboxLabel}
            showPrice={showPrice}
            showQuantity={showQuantity}
            showTrackingInfo={showTrackingInfo}
            showOptions={showOptions}
            showBuyAgain={showBuyAgain}
            order={order}
            onOpenChange={onOpenChange}
          />
        </div>
      ))}
    </div>
  )
})
